/**
 * 🧊 **카메라 AR·책 뷰어에서 진짜 3D** — 새 화면의 「돌려 보는」 층에 3D 파일(.glb)이 있으면 띠 대신
 * **3D 모델을 세운다**(B안 카메라 2026-09-12 · C안 책 뷰어 2026-09-13). A-Frame 이 이미 카메라 AR 에 있어 엔진을 더 싣지 않는다.
 *
 * 여기는 **A-Frame 엔티티에 붙일 값**만 계산한다(순수 함수 — 검사 가능). 판정(어느 층이 도나·얼마나 돌았나·
 * 얼마나 기울고 커졌나)은 shared(`spinTarget`·`spinMode`·`spinFrame`·`spinView`)이고, 이 파일은 그 값을
 * **각도·배율·애니 문자열**로 바꾼다.
 *
 * 🔴 띠와 3D 가 **같은 방향**으로 돌아야 한다 — 띠는 `render-turntable.mjs` 가 `pivot.rotation.y = +deg` 로
 *    찍었고(각도가 늘면 물체가 왼쪽으로 돈다), 여기서도 `rotation.y = turn × 360` 을 같은 부호로 준다.
 * 🔴 「움직임 줄이기」면 저절로 돌지 않는다(끌기는 된다).
 */
import { SPIN_AUTO_FPS } from "@greg-chick/shared";

/** 3D 모델이 책(폭 1) 위에서 차지할 폭 — 새 화면 띠 자리(0.68)와 비슷하게, 책 밖으로 안 나가게. */
export const MODEL_FIT_WIDTH = 0.62;
/** 책 면에서 띄우는 높이(책 폭 기준) — 0 이면 종이에 파묻힌다. */
export const MODEL_LIFT = 0.08;

/** 오버레이/책 뷰어 → 무대: 「이 3D 를 이 각도·이 시점으로」. */
export interface MagicModelReq {
  layerId: string;
  url: string;
  /** 돌린 각도(0~1, shared 판정 그대로) */
  turn: number;
  /** 저절로 도는 중인가(끌기 전) */
  auto: boolean;
  /** 띠의 장 수 — 한 바퀴 시간을 띠와 맞춘다 */
  frames: number;
  /** 🧊 위아래 기울기(도, shared `spinView`) — 없으면 정면 */
  tilt?: number;
  /** 🧊 두 손가락 배율(shared `spinView`) — 없으면 1 */
  zoom?: number;
}

export interface ModelEntityAttrs {
  rotation: string;
  /** 저절로 돌 때만 — A-Frame `animation` 속성 문자열. 없으면 `null`(속성을 지운다). */
  animation: string | null;
  /** 🧊 배율 — 책 폭에 맞춘 값(`modelFit`)에 이만큼 곱한다 */
  zoom: number;
}

/**
 * 돌린 각도(0~1) → A-Frame 회전. `auto` 면 지금 각도에서 이어서 한 바퀴를 계속 돈다.
 * 기울기(`tilt`)는 x 축 — 저절로 돌 때도 기울기는 그대로 둔 채 y 만 돈다.
 * @param frames 띠의 장 수 — 한 바퀴 시간을 띠와 같게(장수 ÷ SPIN_AUTO_FPS 초) 맞춘다
 */
export function modelEntityAttrs(
  turn: number,
  auto: boolean,
  frames: number,
  reducedMotion: boolean,
  tilt = 0,
  zoom = 1,
): ModelEntityAttrs {
  const t = Number.isFinite(turn) ? ((turn % 1) + 1) % 1 : 0;
  const deg = t * 360;
  const tx = Number.isFinite(tilt) ? tilt : 0;
  const z = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
  const rotation = `${tx.toFixed(2)} ${deg.toFixed(2)} 0`;
  if (!auto || reducedMotion) return { rotation, animation: null, zoom: z };
  const durMs = Math.round((Math.max(2, frames) / SPIN_AUTO_FPS) * 1000);
  return {
    rotation,
    animation: `property: rotation; from: ${tx.toFixed(2)} ${deg.toFixed(2)} 0; to: ${tx.toFixed(2)} ${(deg + 360).toFixed(2)} 0; dur: ${durMs}; easing: linear; loop: true`,
    zoom: z,
  };
}

/**
 * 🔄 **지금 3D 가 서 있는 각도 → 돌린 값(0~1)** — `modelEntityAttrs` 의 **정확한 역**이다.
 *
 * ## 🔴 왜 필요한가 (2026-09-12 · 카메라로 직접 걸어 보고 찾았다)
 * 저절로 돌던 것을 아이가 **잡는 순간** 「지금 보이던 각도」에서 이어받아야 한다. 책 뷰어는 띠의
 * CSS 를 읽어(`turnFromTransform`) 이어받는데, **카메라 AR 에는 띠가 없다**(3D 를 세우니까).
 * 그래서 이어받을 것을 못 찾고 **정면(0)으로 탁 튀었다** — 아무 에러도 안 났고, 책 뷰어만
 * 눌러 보는 검사로는 영영 안 보였다.
 *
 * 되돌리는 규칙(부호·한 바퀴 접기)을 딴 데서 다시 짜면 조용히 갈린다 — 정변환 바로 옆에 둔다.
 */
export function turnFromModelRotation(radiansY: number): number {
  if (!Number.isFinite(radiansY)) return 0;
  const turn = (radiansY * 180) / Math.PI / 360;
  return ((turn % 1) + 1) % 1;
}

/**
 * 모델의 실제 크기(three Box3 의 가로·세로·깊이) → 책 위 배율과 자리.
 * 가장 긴 가로·세로가 `MODEL_FIT_WIDTH` 가 되게 키우고, 아래가 책 면에 닿지 않게 살짝 띄운다.
 * @param zoom 🧊 두 손가락 배율 — 맞춘 크기에 곱한다. 띄우는 높이도 같이 커진다(뒷면이 종이를 안 뚫게)
 */
export function modelFit(size: { x: number; y: number; z: number }, zoom = 1): { scale: string; position: string; fitScale: number } {
  const longest = Math.max(size.x, size.y, 1e-6);
  const z = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
  const s = (MODEL_FIT_WIDTH / longest) * z;
  const lift = MODEL_LIFT + (size.z * s) / 2; // 두께의 절반만큼 더 — 뒷면이 종이를 뚫지 않게
  return { scale: `${s.toFixed(4)} ${s.toFixed(4)} ${s.toFixed(4)}`, position: `0 0 ${lift.toFixed(4)}`, fitScale: s };
}

/** A-Frame 의 `gltf-model` 값 — 주소를 그대로 넣으면 `;` `:` 가 속성 문법과 섞인다. `url()` 로 감싼다. */
export function gltfModelAttr(url: string): string {
  return `url(${url})`;
}

/**
 * 💡 **책 위 조명** — 3D 가 「종이에 붙은 그림」이 아니라 「책 위에 놓인 물건」으로 보이게(2026-09-13).
 * 은은한 전체 빛 + 위·앞에서 비스듬히 내려오는 빛 하나(그림자를 만든다). 그림자는 책 면(z=0)에 받는다.
 * 값은 순수 상수 — 무대(카메라·책 뷰어)가 같은 것을 쓴다.
 */
export const MODEL_LIGHTS = {
  ambient: "type: ambient; color: #fff4e0; intensity: 0.85",
  key: "type: directional; color: #ffffff; intensity: 1.15; castShadow: true; shadowMapWidth: 1024; shadowMapHeight: 1024; shadowCameraLeft: -1; shadowCameraRight: 1; shadowCameraTop: 1; shadowCameraBottom: -1; shadowCameraNear: 0.1; shadowCameraFar: 6",
  keyPosition: "0.7 1.1 1.6",
  /** 그림자를 받는 투명 판(책 면) — 색은 없고 그림자만 보인다(`shader: shadow`) */
  ground: "shader: shadow; opacity: 0.35",
} as const;
